import { type SearchCollectionImagesQuery, useSearchCollectionImagesInfiniteQuery } from '@vizit/api';
import { useInfiniteScroll } from '@vizit/hooks';
import { debounce } from '@vizit/utils';
import * as React from 'react';

/**
 * Calculate optimal load count based on viewport dimensions
 */
function calculateOptimalLoadCount(): number {
  const viewport = {
    width: window.innerWidth,
    height: window.innerHeight,
  };

  // Estimate grid columns based on breakpoints (similar to typical responsive grids)
  let columns: number;
  if (viewport.width < 640) {
    columns = 2; // mobile
  } else if (viewport.width < 768) {
    columns = 3; // small tablet
  } else if (viewport.width < 1024) {
    columns = 4; // tablet
  } else if (viewport.width < 1280) {
    columns = 5; // small desktop
  } else {
    columns = 6; // large desktop
  }

  const estimatedCardWidth = viewport.width / columns;
  // Some buffer
  const estimatedCardHeight = estimatedCardWidth * 1.2;

  // Calculate how many rows fit in the viewport (with buffer)
  const availableHeight = viewport.height * 0.8;
  const rowsNeeded = Math.ceil(availableHeight / estimatedCardHeight);

  // Add extra rows to ensure we overfill slightly for better UX
  const bufferRows = 2;
  const totalRows = rowsNeeded + bufferRows;

  const calculatedCount = totalRows * columns;

  const minCount = 12;
  const maxCount = 50;
  return Math.max(minCount, Math.min(maxCount, calculatedCount));
}

function useOptimalInitialLoadCount(): number {
  // Initialize with calculated value to avoid extra render
  const [loadCount, setLoadCount] = React.useState(() => calculateOptimalLoadCount());

  React.useEffect(() => {
    // Debounced resize handler to limit calculations during window resizing
    const debouncedHandleResize = debounce(() => {
      const newCount = calculateOptimalLoadCount();
      setLoadCount(newCount);
    }, 250); // 250ms debounce

    window.addEventListener('resize', debouncedHandleResize);

    return () => {
      window.removeEventListener('resize', debouncedHandleResize);
    };
  }, []);

  return loadCount;
}

interface UseCollectionAssetGridProps {
  /**
   * The ID of the portfolio
   */
  portfolioId: string;
  /**
   * The ID of the collection
   */
  collectionId: string;
  /**
   * Number of items to load per page
   * @default 12
   */
  limit?: number;
  /**
   * Whether to automatically calculate optimal initial load count
   * @default true
   */
  useOptimalInitialLoad?: boolean;
}

/**
 * Hook to fetch collection assets with infinite loading
 *
 * @param props - Hook configuration
 * @returns Collection assets data and loading state
 */
function useCollectionAssetGrid(props: UseCollectionAssetGridProps) {
  const { portfolioId, collectionId, limit = 12, useOptimalInitialLoad = true } = props;

  const optimalInitialCount = useOptimalInitialLoadCount();

  // Use optimal count for initial load, standard limit for subsequent pages
  const effectiveInitialLimit = useOptimalInitialLoad ? optimalInitialCount : limit;

  const { data, isLoading, error, hasNextPage, hasPreviousPage, isFetchingNextPage, fetchNextPage } =
    useSearchCollectionImagesInfiniteQuery(
      {
        portfolio_id: portfolioId,
        collection_id: collectionId,
        limit: effectiveInitialLimit,
        sort_by: 'created_at',
        sort_direction: 'desc',
      },
      {
        enabled: !!portfolioId && !!collectionId,
      },
    );

  const assets = React.useMemo(() => {
    const infiniteData = data as { pages?: SearchCollectionImagesQuery[] } | undefined;
    const allImages = infiniteData?.pages?.flatMap((page) => page.data) || [];
    return allImages;
  }, [data]);

  const totalCount = React.useMemo(() => {
    const infiniteData = data as { pages?: SearchCollectionImagesQuery[] } | undefined;
    return infiniteData?.pages?.[0]?.total_count ?? 0;
  }, [data]);

  const sentinelRef = useInfiniteScroll({ hasNextPage: hasNextPage ?? false, isFetchingNextPage, fetchNextPage });

  // Use hasPreviousPage to determine loading count
  // hasPreviousPage = false: initial load (show optimal count)
  // hasPreviousPage = true: subsequent loads (show standard limit)
  const loadingCount = React.useMemo(() => {
    if (isLoading && !hasPreviousPage) {
      // Initial load: show optimal initial count skeletons (e.g., 24)
      return effectiveInitialLimit;
    }
    if (isFetchingNextPage) {
      // Subsequent loads: show standard limit skeletons (e.g., 12)
      return limit;
    }
    // Not loading: no skeletons needed
    return 0;
  }, [isLoading, hasPreviousPage, isFetchingNextPage, effectiveInitialLimit, limit]);

  return {
    assets,
    totalCount,
    isLoading,
    hasError: !!error,
    hasNextPage,
    isFetchingNextPage,
    sentinelRef,
    loadingCount,
  };
}

export { useCollectionAssetGrid };
export type { UseCollectionAssetGridProps };
